import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DeleteIcon, EditIcon, EyeIcon } from "../../assets/icons/Svgs";
import { eliminarGrupo, exportarUsernameGrupo } from "../../services/grupo.service";
import {
  showDeletedAlert,
  showErrorAlert,
  showQuestionDeleteAlert,
  showUpdateAlert,
} from "../../components/molecules/ShowAlert";
import { getHorarioGruposByGrupo } from "../../services/horarioGrupo";
import { getAllHorarios } from "../../services/horario.service";

// Ícono de descarga
const DownloadIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

export const GrupoCard = ({ grupo = {}, onDelete }) => {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);

  // Horarios "live" (se cargan on-demand al abrir el panel)
  const [horariosLive, setHorariosLive] = useState([]);
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  const detailsRef = useRef(null);

  // NO usamos grupo.horarios
  const {
    idGrupo,
    nombre = "",
    nombreAntGrupo = null,
    cupos = 0,
    modulo = { nombre: "", sigla: "" },
    docente = { nombre: "", apellido: "" },
    turno = { nombre: "" },
    modalidad = { nombre: "" },
    cuentaZoom = "",
    contrasenaZoom = "",
    nombreAuxiliar = "_",
    celularAuxiliar = "_",
    fechaInicio,
    fechaFin,
  } = grupo;

  const formatearFecha = (fecha) => {
    if (!fecha) return "_";
    const date = new Date(fecha);
    return date.toLocaleDateString("es-BO", { year: "numeric", month: "2-digit", day: "2-digit" });
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => alert("Copiado al portapapeles"))
      .catch(() => alert("Error al copiar"));
  };

  const fetchHorarios = async () => {
    if (!idGrupo) return;
    try {
      setLoadingHorarios(true);
      const [hgList, catalogo] = await Promise.all([
        getHorarioGruposByGrupo(idGrupo),
        getAllHorarios(),
      ]);
      const catMap = new Map((catalogo || []).map((c) => [c.idHorario, c]));
      const compuestos = (hgList || []).map((item) => {
        const cat = item.idHorario ? catMap.get(item.idHorario) : null;
        let horaInicio = item.horaInicio || cat?.horaInicio || cat?.horario?.horaInicio || "?";
        let horaFin = item.horaFin || cat?.horaFin || cat?.horario?.horaFin || "?";
        let dia = item.dia;
        if (!dia && cat) {
          if (typeof cat.dia === "string") {
            dia = cat.dia;
          } else if (typeof cat.dia === "object" && cat.dia !== null) {
            dia = cat.dia.nombre || "?";
          } else {
            dia = cat?.horario?.dia?.nombre || cat?.horario?.dia || "?";
          }
        }
        if (!dia) dia = "?";
        return {
          id: item.id,
          horaInicio,
          horaFin,
          dia,
        };
      });

      setHorariosLive(compuestos);
    } catch (e) {
      console.error("Error trayendo horarios del grupo:", e);
      setHorariosLive([]);
    } finally {
      setLoadingHorarios(false);
    }
  };
  useEffect(() => {
    const el = detailsRef.current;
    if (!el) return;
    const onToggle = () => {
      if (el.open) fetchHorarios();
    };
    el.addEventListener("toggle", onToggle);
    return () => el.removeEventListener("toggle", onToggle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idGrupo]);

  const handleDelete = async () => {
    if (!idGrupo || deleting) return;

    showQuestionDeleteAlert({
      title: "¿Eliminar grupo?",
      text: `Esta acción eliminará el grupo "${nombre}".`,
      callback: async (result) => {
        if (!result.isConfirmed) return;
        try {
          setDeleting(true);
          await eliminarGrupo(idGrupo);
          showDeletedAlert({
            text: "Grupo eliminado correctamente.",
            callback: () => onDelete?.(idGrupo),
          });
        } catch (error) {
          console.error("Error al eliminar el grupo:", error);
          showErrorAlert({ text: "No se pudo eliminar el grupo. Intenta nuevamente." });
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleUpdate = () => {
    showUpdateAlert({ callback: () => navigate(`/grupos/actualizar/${idGrupo}`) });
  };

  const handleViewStudents = () => {
    navigate(`/grupos/${idGrupo}/EstudiantesGrupo`);
  };

  const handleExportUsernames = async () => {
    try {
      await exportarUsernameGrupo(idGrupo);
    } catch (error) {
      console.error("Error al exportar usernames:", error);
      alert("Error al descargar el archivo");
    }
  };

  const renderDato = (label, value, extraButton = null) => (
    <p className="font-semibold">
      {label}:{" "}
      <span className="font-normal break-words whitespace-normal">
        {value || "_"} {extraButton}
      </span>
    </p>
  );

  return (
    <li className="w-full h-full flex flex-col shadow-lg rounded-lg p-6 bg-white">
      <h3 className="text-xl font-semibold text-theme-blue text-center mb-4">
        {nombre}
      </h3>

      <div className="mb-4 space-y-2">
        {renderDato("Cupos", cupos)}
        {renderDato("Módulo", `${modulo.nombre} / ${modulo.sigla}`)}
        {renderDato("Docente", `${docente.nombre} ${docente.apellido}`)}
        {renderDato("Turno", turno.nombre)}
        {renderDato("Modalidad", modalidad.nombre)}
        {renderDato("Fecha de inicio", formatearFecha(fechaInicio))}
        {renderDato("Fecha fin", formatearFecha(fechaFin))}

        {cuentaZoom && renderDato(
          "Link de Zoom",
          <a href={cuentaZoom} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
            Link Zoom
          </a>,
          <button onClick={() => copyToClipboard(cuentaZoom)} className="ml-2 text-sm text-gray-600 hover:text-gray-800">
            Copiar
          </button>
        )}

        {contrasenaZoom && renderDato(
          "Contraseña de Zoom",
          contrasenaZoom,
          <button onClick={() => copyToClipboard(contrasenaZoom)} className="ml-2 text-sm text-gray-600 hover:text-gray-800">
            Copiar
          </button>
        )}

        {renderDato("Nombre auxiliar", nombreAuxiliar)}
        {renderDato("Número auxiliar", celularAuxiliar)}

        {nombreAntGrupo && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Nombre anterior del grupo:</span> {nombreAntGrupo}
            </p>
            {nombreAntGrupo !== nombre ? (
              <p className="text-xs text-green-600 italic">El nombre fue modificado</p>
            ) : (
              <p className="text-xs text-gray-500 italic">(Mismo nombre actual)</p>
            )}
          </div>
        )}
      </div>

      <details ref={detailsRef} className="relative mb-4">
        <summary className="cursor-pointer font-semibold text-theme-blue hover:text-blue-700 w-fit">
          Horarios
        </summary>

        <div className="absolute left-0 w-full mt-2 bg-white border border-gray-300 p-2 rounded-md z-10 shadow-lg max-h-48 overflow-y-auto">
          {loadingHorarios ? (
            <p className="text-center text-gray-500">Cargando...</p>
          ) : horariosLive.length > 0 ? (
            <ul className="space-y-2">
              {horariosLive.map((h) => (
                <li key={h.id} className="flex justify-between">
                  {`${h.horaInicio} - ${h.horaFin} - ${h.dia}`}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-center">No tiene horarios asignados.</p>
          )}
          <div className="text-right mt-2">
            <button
              type="button"
              onClick={fetchHorarios}
              className="text-xs underline text-blue-600 hover:text-blue-800"
            >
              Refrescar
            </button>
          </div>
        </div>
      </details>

      <div className="flex justify-start gap-4 mt-auto">
        <button
          onClick={handleUpdate}
          className="bg-green-500 hover:bg-green-600 p-2 rounded-md text-white transition-colors"
          title="Editar grupo"
        >
          <EditIcon className="w-5 h-5" />
        </button>

        <button
          onClick={handleDelete}
          disabled={deleting || !idGrupo}
          className={`p-2 rounded-md text-white transition-colors ${deleting ? "bg-red-300" : "bg-red-500 hover:bg-red-600"}`}
          title="Eliminar grupo"
        >
          {deleting ? "Eliminando..." : <DeleteIcon className="w-5 h-5" />}
        </button>

        <button
          onClick={handleViewStudents}
          className="bg-blue-500 hover:bg-blue-600 p-2 rounded-md text-white transition-colors"
          title="Ver estudiantes"
        >
          <EyeIcon className="w-5 h-5" />
        </button>

        <button
          onClick={handleExportUsernames}
          className="bg-yellow-500 hover:bg-yellow-600 p-2 rounded-md text-white transition-colors"
          title="Descargar Username"
        >
          <DownloadIcon className="w-5 h-5" />
        </button>
      </div>
    </li>
  );
};
